import {Component, OnInit, computed, inject, signal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Subject, debounceTime} from 'rxjs';
import {SelectComponent} from '../../../_ui/components/controls/select/select.component';
import {describeAccountActionError} from '../../../_shared/errors/account-action-errors';
import {AccountAdminHttpService} from '../_services/account-admin-http.service';
import {
  ACCOUNT_IDENTITY_PROVIDERS,
  AccountIdentity,
  accountName,
  hasSignedIn,
  providerLabel
} from '../_models/account-identity';
import {TwoFactorResetDialogComponent} from './two-factor-reset-dialog.component';

/** One entry of a `cqme-select`. That control is generic over `TItem extends object`, so a bare string cannot. */
interface Option<T> {
  value: T;
  label: string;
}

/** The three states of the 2FA filter. `'all'` is the absence of the query parameter, not a third value. */
type TwoFactorFilter = 'all' | 'on' | 'off';

/**
 * **Konten** — the platform-owner account directory, and the audited second-factor reset performed from it
 * (#279 completing #282).
 *
 * ### What it is for
 *
 * Recovery codes are the self-service half of "I lost my authenticator". This screen is the other half: when
 * somebody has lost both the phone and the printout, a platform owner takes the factor away so they can enrol
 * a new one. It is the same shape as the admin-reset-only employee PIN — an administrator may remove a
 * credential so a new one can be set, and may never read or choose the one that was there.
 *
 * ### Why it is platform-owner only, and lives in the owner shell
 *
 * The listing enumerates **every account on the installation**, across every tenant, and the reset acts on one
 * by bare id with no tenant read at all. There is nothing here to confine a company-scoped role to, so naming
 * one would hand one company's administrator every other company's accounts — exactly the reasoning that keeps
 * `AccountController.AllIdentities` platform-only, and the reason Bug #418 had to narrow the employee link
 * picker.
 *
 * ### Filtering is the server's job
 *
 * Every filter is a query parameter. The alternative — fetching the whole table and narrowing in the browser —
 * is what the admin link picker does, and it is acceptable there only because a picker is scrolled by a human.
 * Here it would put a copy of every account on the deployment into an operator's browser to answer a question
 * about one of them. The two text boxes are debounced ({@link DEBOUNCE_MS}) so typing a name is one request,
 * not one per keystroke; the two selects re-request immediately, because a select is a single decision.
 *
 * ### The reset asks first, outside the row
 *
 * {@link TwoFactorResetDialogComponent} is mounted once for the whole screen, driven off {@link pendingReset},
 * and never rendered inside the `<td>` that triggered it — growing a row shifts every row below it under the
 * cursor, which is how somebody confirms the wrong account. The action is irreversible, so that matters here
 * more than almost anywhere.
 */
@Component({
  selector: 'owner-account-administration',
  standalone: true,
  imports: [DatePipe, SelectComponent, TwoFactorResetDialogComponent],
  template: `
    <div class="mx-auto max-w-6xl space-y-6 p-4">

      <div>
        <h1 class="text-2xl font-bold tracking-tight text-gray-900">Konten</h1>
        <p class="mt-1 text-sm text-gray-500">
          Alle Konten dieser Installation. Setzen Sie hier die Zwei-Faktor-Anmeldung eines Kontos zurück, wenn
          die Person weder ihre Authenticator-App noch ihre Wiederherstellungscodes hat.
        </p>
      </div>

      <!-- ── Filter ───────────────────────────────────────────────────────────── -->
      <div class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        <div class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div>
            <label for="account-provider" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
              Anbieter
            </label>
            <!-- Clearable, with the placeholder standing for "no filter": an explicit "Alle" entry would be a
                 fourth value the request has to map back to absence. The "|| undefined" is load-bearing —
                 ng-select treats '' as a DEFINED value, fails to match it, and renders a blank selected box
                 where the placeholder belongs. -->
            <cqme-select id="account-provider" size="md" [options]="providerOptions" [labelKey]="'label'"
                         [valueKey]="'value'" [clearable]="true" placeholder="Alle Anbieter"
                         ariaLabel="Anbieter, nach dem gefiltert wird"
                         [current]="provider() || undefined"
                         (currentChange)="setProvider(one($event))"/>
          </div>

          <div>
            <label for="account-email" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
              E-Mail
            </label>
            <input id="account-email" type="search" [value]="emailText()"
                   (input)="setEmail(value($event))" placeholder="Teil der Adresse"
                   class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm
                          shadow-sm transition-all duration-200 placeholder-gray-400
                          focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500">
          </div>

          <div>
            <label for="account-name" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
              Name
            </label>
            <input id="account-name" type="search" [value]="nameText()"
                   (input)="setName(value($event))" placeholder="Teil des Namens"
                   class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm
                          shadow-sm transition-all duration-200 placeholder-gray-400
                          focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500">
          </div>

          <div>
            <label for="account-2fa" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
              Zwei-Faktor
            </label>
            <!-- Not clearable: "Alle" IS the unfiltered state, so there is nothing for a clear button to
                 return to and offering one would give the same state two spellings. -->
            <cqme-select id="account-2fa" size="md" [options]="twoFactorOptions" [labelKey]="'label'"
                         [valueKey]="'value'" ariaLabel="Zustand der Zwei-Faktor-Anmeldung"
                         [current]="twoFactor()" (currentChange)="setTwoFactor(one($event))"/>
          </div>
        </div>

        <p class="mt-3 text-sm text-gray-500">
          Die Suche nach E-Mail und Name findet Teiltreffer und beachtet Groß- und Kleinschreibung nicht.
        </p>
      </div>

      @if (actionError(); as message) {
        <p class="rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">
          {{ message }}
        </p>
      }

      @if (listError(); as message) {
        <p class="rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">
          {{ message }}
          <button type="button" (click)="load()" class="underline">Erneut versuchen</button>
        </p>
      }

      <!-- ── Results ──────────────────────────────────────────────────────────── -->
      @if (loading()) {
        <p id="accounts-loading" class="py-8 text-center text-sm text-gray-500" role="status" aria-live="polite">
          Konten werden geladen …
        </p>
      } @else if (!identities().length) {
        <p id="accounts-empty"
           class="rounded-2xl border-2 border-dashed border-gray-100 py-6 text-center text-sm italic
                  text-gray-500">
          {{ filtered() ? 'Keine Konten passen zu diesen Filtern.' : 'Es gibt noch keine Konten.' }}
        </p>
      } @else {
        <p class="text-sm text-gray-500">{{ identities().length }} Konten</p>

        <!-- tabindex is not decoration: a container that scrolls has to be reachable by keyboard, or the
             columns past the fold are unreachable without a mouse (axe: scrollable-region-focusable). -->
        <div id="accounts-table" tabindex="0"
             class="hidden overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-sm md:block">
          <table class="min-w-full divide-y divide-gray-100 text-sm">
            <thead class="bg-gray-50">
              <tr class="text-left text-xs uppercase tracking-wide text-gray-500">
                <th scope="col" class="px-4 py-3 font-semibold">Konto</th>
                <th scope="col" class="px-4 py-3 font-semibold">Anbieter</th>
                <th scope="col" class="px-4 py-3 font-semibold">2FA</th>
                <th scope="col" class="px-4 py-3 font-semibold">Verknüpfte Konten</th>
                <th scope="col" class="px-4 py-3 font-semibold">Zuletzt angemeldet</th>
                <th scope="col" class="px-4 py-3 font-semibold">Status</th>
                <th scope="col" class="px-4 py-3 text-right font-semibold">Aktion</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              @for (identity of identities(); track identity.id) {
                <tr class="transition-colors hover:bg-gray-50">
                  <td class="px-4 py-3">
                    <span class="font-medium text-gray-900">{{ name(identity) }}</span>
                    @if (identity.email && identity.email !== name(identity)) {
                      <span class="block text-gray-500">{{ identity.email }}</span>
                    }
                  </td>
                  <td class="px-4 py-3 text-gray-700">{{ label(identity.provider) }}</td>
                  <td class="px-4 py-3">
                    <span class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
                          [class]="identity.twoFactorEnabled
                                     ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-700'">
                      {{ identity.twoFactorEnabled ? 'Aktiviert' : 'Keine' }}
                    </span>
                  </td>
                  <td class="px-4 py-3 text-gray-700">{{ identity.linkedAccounts }}</td>
                  <td class="px-4 py-3 text-gray-500">
                    {{ signedIn(identity) ? (identity.lastLoginUtc | date:'dd.MM.yy HH:mm') : '–' }}
                  </td>
                  <td class="px-4 py-3">
                    <span class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
                          [class]="identity.active
                                     ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'">
                      {{ identity.active ? 'Aktiv' : 'Deaktiviert' }}
                    </span>
                  </td>
                  <td class="px-4 py-3 text-right">
                    <button type="button" (click)="askReset(identity)"
                            [disabled]="!identity.twoFactorEnabled || busy()"
                            [attr.aria-label]="resetLabel(identity)"
                            class="text-xs font-semibold text-red-700 underline
                                   disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline">
                      2FA zurücksetzen
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <ul id="accounts-cards" class="grid grid-cols-1 gap-3 md:hidden">
          @for (identity of identities(); track identity.id) {
            <li class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
              <div class="flex items-start justify-between gap-2">
                <span class="font-bold tracking-tight text-gray-800">{{ name(identity) }}</span>
                <span class="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-semibold"
                      [class]="identity.active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'">
                  {{ identity.active ? 'Aktiv' : 'Deaktiviert' }}
                </span>
              </div>
              @if (identity.email && identity.email !== name(identity)) {
                <p class="text-sm text-gray-500">{{ identity.email }}</p>
              }
              <p class="mt-1 text-sm text-gray-700">{{ label(identity.provider) }}</p>
              <p class="text-sm text-gray-500">
                Zwei-Faktor: {{ identity.twoFactorEnabled ? 'Aktiviert' : 'Keine' }}
              </p>
              <p class="text-sm text-gray-500">Verknüpfte Konten: {{ identity.linkedAccounts }}</p>
              <p class="text-sm text-gray-500">
                Zuletzt angemeldet:
                {{ signedIn(identity) ? (identity.lastLoginUtc | date:'dd.MM.yy HH:mm') : '–' }}
              </p>
              <div class="mt-3 flex flex-wrap gap-3">
                <button type="button" (click)="askReset(identity)"
                        [disabled]="!identity.twoFactorEnabled || busy()"
                        [attr.aria-label]="resetLabel(identity)"
                        class="text-xs font-semibold text-red-700 underline
                               disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline">
                  2FA zurücksetzen
                </button>
              </div>
            </li>
          }
        </ul>
      }
    </div>

    <!-- One instance for the table and the card list alike — see "The reset asks first" on the class. -->
    @if (pendingReset(); as pending) {
      <owner-two-factor-reset-dialog [account]="pending" [busy]="busy()"
                                     (confirmed)="confirmReset()" (cancelled)="cancelReset()"/>
    }
  `
})
export class AccountAdministrationComponent implements OnInit {
  /**
   * How long the two text filters wait before asking the server.
   *
   * 300 ms is the usual "stopped typing" threshold: long enough that an eight-letter name is one request
   * rather than eight, short enough that the list does not feel detached from the keyboard. It is a constant
   * rather than a literal so the spec can drive it with the same number the component uses.
   */
  static readonly DEBOUNCE_MS = 300;

  private readonly http = inject(AccountAdminHttpService);

  /** Fires on every keystroke in the two text boxes; debounced into one load. */
  private readonly typed = new Subject<void>();

  protected readonly providerOptions: Option<string>[] =
    ACCOUNT_IDENTITY_PROVIDERS.map(provider => ({value: provider, label: providerLabel(provider)}));

  protected readonly twoFactorOptions: Option<TwoFactorFilter>[] = [
    {value: 'all', label: 'Alle'},
    {value: 'on', label: 'Aktiviert'},
    {value: 'off', label: 'Deaktiviert'}
  ];

  protected readonly identities = signal<AccountIdentity[]>([]);
  protected readonly loading = signal(false);
  protected readonly busy = signal(false);
  protected readonly listError = signal<string | null>(null);
  protected readonly actionError = signal<string | null>(null);

  /** Set while the confirmation dialog is on screen; also names the account the confirm acts on. */
  protected readonly pendingReset = signal<AccountIdentity | null>(null);

  protected readonly provider = signal<string>('');
  protected readonly emailText = signal('');
  protected readonly nameText = signal('');
  protected readonly twoFactor = signal<TwoFactorFilter>('all');

  /** Whether anything is narrowing the list — decides which of the two empty sentences is true. */
  protected readonly filtered = computed(() =>
    !!this.provider() || !!this.emailText().trim() || !!this.nameText().trim() || this.twoFactor() !== 'all');

  protected readonly label = providerLabel;
  protected readonly name = accountName;
  protected readonly signedIn = hasSignedIn;

  constructor() {
    this.typed
      .pipe(debounceTime(AccountAdministrationComponent.DEBOUNCE_MS), takeUntilDestroyed())
      .subscribe(() => this.load());
  }

  ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    this.loading.set(true);
    this.listError.set(null);

    this.http.list({
      provider: this.provider() || null,
      email: this.emailText().trim() || null,
      name: this.nameText().trim() || null,
      twoFactor: this.twoFactor() === 'all' ? null : this.twoFactor() === 'on'
    }).subscribe({
      next: identities => {
        this.identities.set(identities);
        this.loading.set(false);
      },
      error: err => {
        this.loading.set(false);
        // Cleared too: a stale list under a fresh error message reads as "these are the results", which is
        // exactly what they are not.
        this.identities.set([]);
        this.listError.set(describeAccountActionError(err, 'Die Konten konnten nicht geladen werden.'));
      }
    });
  }

  // ----- filters -----

  protected setProvider(value: string): void {
    this.provider.set(value);
    this.load();
  }

  protected setTwoFactor(value: string): void {
    this.twoFactor.set(value === 'on' || value === 'off' ? value : 'all');
    this.load();
  }

  protected setEmail(value: string): void {
    this.emailText.set(value);
    this.typed.next();
  }

  protected setName(value: string): void {
    this.nameText.set(value);
    this.typed.next();
  }

  // ----- the reset -----

  protected askReset(identity: AccountIdentity): void {
    // Restated rather than trusted from the template's `[disabled]`: this is the entry point of an
    // irreversible action, and a disabled attribute is a rendering decision, not a guard.
    if (!identity.twoFactorEnabled) return;
    this.actionError.set(null);
    this.pendingReset.set(identity);
  }

  /** Cancel button, backdrop click or Escape inside the dialog — all arrive here, all change nothing. */
  protected cancelReset(): void {
    this.pendingReset.set(null);
  }

  protected confirmReset(): void {
    const pending = this.pendingReset();
    if (!pending || this.busy()) return;

    this.busy.set(true);
    this.actionError.set(null);

    this.http.resetSecondFactor(pending.id).subscribe({
      next: () => {
        this.busy.set(false);
        this.pendingReset.set(null);
        // Re-read rather than patching the row in place: the reset is not the only thing that can have moved
        // since the list was fetched, and a screen that shows one row's new truth beside six rows of old truth
        // is worse than one that is simply current.
        this.load();
      },
      error: err => {
        this.busy.set(false);
        this.pendingReset.set(null);
        this.actionError.set(describeAccountActionError(err,
          'Die Zwei-Faktor-Anmeldung konnte nicht zurückgesetzt werden.'));
        // Both named refusals mean the row on screen is out of date — the account is gone, or somebody else
        // reset it first — so the list is re-read either way. The message stays: `load()` clears `listError`,
        // not `actionError`.
        this.load();
      }
    });
  }

  // ----- display helpers -----

  protected value(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  /**
   * Narrows what a single-value `cqme-select` emits down to the one string these filters store.
   *
   * The control is declared to emit `TValue | TValue[] | undefined` because the same component also does
   * multi-select; neither filter is `[multiple]`, so an array can never arrive. Clearing can, as `undefined`,
   * which both call sites already read as "no filter".
   */
  protected one(value: string | string[] | undefined): string {
    return (Array.isArray(value) ? value[0] : value) ?? '';
  }

  /**
   * The accessible name of a row's reset button.
   *
   * Every row's visible label is the same three words, so a screen reader moving button to button would hear
   * "2FA zurücksetzen" repeatedly with nothing saying which account each one belongs to. The account name is
   * in the row for a sighted reader; this is how it reaches everybody else.
   */
  protected resetLabel(identity: AccountIdentity): string {
    return `Zwei-Faktor-Anmeldung von ${accountName(identity)} zurücksetzen`;
  }
}
